import React, { useState, useEffect, useMemo } from "react";
import { Form } from "@quillforms/renderer-core";
import "@quillforms/renderer-core/build-style/style.css";
import { registerCoreBlocks } from "@quillforms/react-renderer-utils";
import { getServiceList } from "../src/api/services";
import "./LoadingStyles.css";
import { sendTypeForm } from "api/forms";

registerCoreBlocks();

// Sanitize string to prevent XSS
const sanitizeString = (str) => {
  if (typeof str !== 'string') return str;
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
};

const App = () => {

  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const serviceList = await getServiceList();
        setServices(serviceList);
      } catch (err) {
        setError(err.message || 'Failed to load services');
      } finally {
        setIsLoading(false);
      }
    };

    fetchServices();
  }, []);

  const formObj = useMemo(() => ({
    blocks: [
            {
              name: "welcome-screen",
              id: "welcome",
              attributes: {
                label: "Transform your urban development concepts into executed plans with our specialized engineering solutions.",
                // description: "This is just a description",
                attachment: {
                  type: "image",
                  url: "/logo_disaica_negativo.svg",
                },
              },
            },
            {
              name: "short-text",
              id: "name",
              attributes: {
                required: true,
                label: "Let's start with your full name",
              },
            },
            {
              name: "email",
              id: "email",
              attributes: {
                required: true,
                label:
                  "Thanks {{field:name}}, please insert your email to keep in touch.",
              },
            },
            {
              name: "number",
              id: "contact",
              attributes: {
                required: true,
                label: "Please insert your contact number. (International format, only numbers)",
              },
            },
            {
              name: "short-text",
              id: "company",
              attributes: {
                required: true,
                label: "What's your company name?",
              },
            },
            {
              name: "multiple-choice",
              id: "service",
              attributes: {
                required: true,
                multiple: false,
                verticalAlign: false,
                label: "Which service are you interested in?",
                choices: services.map((service) => {
                  return {
                    label: service.name,
                    value: service.name,
                  };
                }),
              },
            },
            {
              name: "short-text",
              id: "location",
              attributes: {
                required: true,
                label:
                  "What is the location of your project? (City, State, Country)",
              },
            },
            {
              name: "short-text",
              id: "size",
              attributes: {
                required: true,
                label:
                  "What is the estimated size of the project area? (Square footage or other relevant metric)",
              },
            },
            {
              name: "date",
              id: "timeline",
              attributes: {
                required: true,
                label:
                  "What is the timeline you are looking at for project completion?",
              },
            },
            {
              name: "long-text",
              id: "description",
              attributes: {
                required: true,
                label: "Can you provide a brief description of your project?",
              },
            },
            {
              name: "date",
              id: "meet",
              attributes: {
                required: true,
                label:
                  "Would you like to schedule a consultation meeting to discuss your project in detail?",
              },
            },
            {
              name: "statement",
              id: "confirmation",
              attributes: {
                label:
                  "Thank you for the information! We will get back to you in a few days.",
                buttonText: "Continue",
                quotationMarks: true,
              },
            },
          ],
    settings: {
      animationDirection: "vertical",
      disableWheelSwiping: false,
      disableNavigationArrows: false,
      disableProgressBar: false,
    },
    theme: {
      font: "Roboto",
      buttonsBgColor: "#868686",
      logo: {
        src: "",
      },
      backgroundColor: "#000",
      questionsColor: "#fff",
      answersColor: "#0aa7c2",
      buttonsFontColor: "#fff",
      buttonsBorderRadius: 25,
      errorsFontColor: "#fff",
      errorsBgColor: "#f00",
      progressBarFillColor: "#000",
      progressBarBgColor: "#ccc",
    },
  }), [services]);

  const handleSubmit = (data, { completeForm, setIsSubmitting }) => {
    if (process.env.NODE_ENV === 'development') {
      console.log("Form submitted with data: ", data);
    }
    setIsSubmitting(true);
    setSubmitError(null);

    // Validate and format the data
    try {
      if (!data.answers || typeof data.answers !== 'object') {
        throw new Error('Invalid form data structure');
      }

      const getFieldValue = (fieldName, defaultValue = '') => {
        const field = data.answers[fieldName];
        if (!field || field.value === undefined || field.value === null) {
          throw new Error(`Missing required field: ${fieldName}`);
        }
        return field.value;
      };

      const serviceValue = getFieldValue('service');
      if (!Array.isArray(serviceValue) || serviceValue.length === 0) {
        throw new Error('Service selection is required');
      }

      const formattedData = {
        name: sanitizeString(getFieldValue('name')),
        email: sanitizeString(getFieldValue('email')),
        contact: JSON.stringify(getFieldValue('contact')),
        service: sanitizeString(serviceValue[0]),
        location: sanitizeString(getFieldValue('location')),
        size: sanitizeString(getFieldValue('size')),
        timeline: getFieldValue('timeline'),
        description: sanitizeString(getFieldValue('description')),
        meet: getFieldValue('meet'),
      };

      sendTypeForm(formattedData)
        .then((response) => {
          setIsSubmitting(false);
          if (process.env.NODE_ENV === 'development') {
            console.log("Form submitted successfully:", response);
          }

          if (response === "OK" || response === "Created") {
            completeForm();
          } else {
            setSubmitError(response || 'Server validation failed. Please try again.');
          }
        })
        .catch((error) => {
          setIsSubmitting(false);
          if (process.env.NODE_ENV === 'development') {
            console.error("There was a problem with the form submission:", error);
          }
          setSubmitError('An error occurred while submitting the form. Please try again.');
        });
    } catch (validationError) {
      setIsSubmitting(false);
      if (process.env.NODE_ENV === 'development') {
        console.error("Form validation error:", validationError);
      }
      setSubmitError(validationError.message || 'Form validation failed');
    }
  };

  if (isLoading) {
    return (
      <div className="loadingContainer">
        <img
          src="/logo_disaica_negativo.svg"
          alt="Loading"
          className="loadingImage"
        />
        <div className="loadingTextWithDots">
          Loading
          <div className="dotsContainer">
            <div className="dot"></div>
            <div className="dot"></div>
            <div className="dot"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#000",
        color: "#fff",
        flexDirection: "column",
        padding: "20px"
      }}>
        <h2>Error Loading Form</h2>
        <p>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: "10px 20px",
            backgroundColor: "#0aa7c2",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
            cursor: "pointer",
            marginTop: "10px"
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", height: "100vh", position: "relative" }}>
      {submitError && (
        <div style={{
          position: "absolute",
          top: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          backgroundColor: "#f44336",
          color: "#fff",
          padding: "15px 20px",
          borderRadius: "5px",
          zIndex: 1000,
          maxWidth: "90%",
          textAlign: "center",
          boxShadow: "0 2px 10px rgba(0,0,0,0.3)"
        }}>
          <strong>Error:</strong> {submitError}
          <button
            onClick={() => setSubmitError(null)}
            style={{
              marginLeft: "15px",
              background: "none",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              fontWeight: "bold"
            }}
          >
            ✕
          </button>
        </div>
      )}
      <Form
        formId={1}
        formObj={formObj}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

export default App;
